import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _styled from '@emotion/styled-base';
import React__default, { forwardRef } from 'react';
import { Spinner } from '@blueprintjs/core';
import { HTMLHeading } from '../html-heading/html-heading.js';
import { Button } from '../button/button.js';
import { TooltipV2 } from '../tooltip-v2/tooltip-v2.js';
import { intentPrimaryTranslucentHover, intentSuccessTranslucentHover, intentWarningTranslucentHover, intentDangerTranslucentHover, neutralTranslucentHover, borderRadiusLarge, intentPrimary, intentSuccess, intentWarning, intentDanger, neutralActive, maxWidthContent, textHeading } from '../theme/theme.js';
import { InformationFilled32, CheckmarkFilled32, WarningAltFilled32, WarningFilled32 } from '@carbon/icons-react';
import cx from 'classnames';

var _excluded = ["action", "actionIcon", "actionText", "children", "className", "hideActionText", "icon", "inPage", "intent", "loading", "title", "titleTagName"];

var CalloutWrapper = /*#__PURE__*/_styled("div", {
  target: "eoyu6sb0"
})("position:relative;display:flex;flex-direction:row;gap:1rem;width:100%;padding:", function (_ref) {
  var inPage = _ref.inPage;
  return inPage ? "1rem" : "0.625rem 1rem";
}, ";padding-left:", function (_ref2) {
  var hasIcon = _ref2.hasIcon;
  return hasIcon && "2.75rem";
}, ";background-color:", function (_ref3) {
  var intent = _ref3.intent;
  return intent === "primary" ? intentPrimaryTranslucentHover : intent === "success" ? intentSuccessTranslucentHover : intent === "warning" ? intentWarningTranslucentHover : intent === "danger" ? intentDangerTranslucentHover : neutralTranslucentHover;
}, ";border-radius:", function (_ref4) {
  var inPage = _ref4.inPage;
  return !inPage && borderRadiusLarge;
}, ";box-shadow:", function (_ref5) {
  var inPage = _ref5.inPage,
      intent = _ref5.intent;
  return inPage ? "none" : intent === "primary" ? "inset 0 0 0 1px ".concat(intentPrimary) : intent === "success" ? "inset 0 0 0 1px ".concat(intentSuccess) : intent === "warning" ? "inset 0 0 0 1px ".concat(intentWarning) : intent === "danger" ? "inset 0 0 0 1px ".concat(intentDanger) : "inset 0 0 0 1px ".concat(neutralActive);
}, ";.vds-callout-v2-title{margin-bottom:0.5rem;}");

var CalloutV2Content = /*#__PURE__*/_styled("div", {
  target: "eoyu6sb1"
})("position:relative;flex:1 1 auto;max-width:", function (_ref6) {
  var inPage = _ref6.inPage;
  return inPage && maxWidthContent;
}, ";margin:0 auto;");

var CalloutV2Action = /*#__PURE__*/_styled("div", {
  target: "eoyu6sb2"
})(".vds-callout-v2-action-button{margin-top:", function (_ref7) {
  var inPage = _ref7.inPage;
  return inPage ? "-0.375rem" : "-0.125rem";
}, ";margin-right:", function (_ref8) {
  var inPage = _ref8.inPage;
  return inPage ? "0" : "-0.25rem";
}, ";}");

var CalloutV2Icon = /*#__PURE__*/_styled("span", {
  target: "eoyu6sb3"
})("position:absolute;top:0.125rem;left:-1.75rem;display:inline-block;flex:0 0 auto;width:1rem;height:1rem;vertical-align:text-bottom;color:", function (_ref9) {
  var intent = _ref9.intent;
  return intent === "primary" ? intentPrimary : intent === "success" ? intentSuccess : intent === "warning" ? intentWarning : intent === "danger" ? intentDanger : textHeading;
}, ";svg{display:block;width:1rem;height:1rem;overflow:visible;}");

var CalloutV2 = /*#__PURE__*/forwardRef(function (_ref10, ref) {
  var action = _ref10.action,
      actionIcon = _ref10.actionIcon,
      actionText = _ref10.actionText,
      children = _ref10.children,
      className = _ref10.className,
      hideActionText = _ref10.hideActionText,
      IconIncoming = _ref10.icon,
      inPage = _ref10.inPage,
      intent = _ref10.intent,
      loading = _ref10.loading,
      title = _ref10.title,
      _ref10$titleTagName = _ref10.titleTagName,
      titleTagName = _ref10$titleTagName === void 0 ? "h4" : _ref10$titleTagName,
      props = _objectWithoutProperties(_ref10, _excluded);

  var combinedClassName = cx("vds-callout-v2", className);

  if (action && hideActionText && !actionIcon) {
    throw new Error("An actionIcon is required when hiding action text.");
  }

  return /*#__PURE__*/React__default.createElement(CalloutWrapper, _extends({
    className: combinedClassName,
    hasIcon: loading || IconIncoming !== null,
    intent: intent,
    inPage: inPage,
    ref: ref
  }, props), /*#__PURE__*/React__default.createElement(CalloutV2Content, {
    className: "vds-callout-v2-content",
    inPage: inPage
  }, loading ? /*#__PURE__*/React__default.createElement(CalloutV2Icon, {
    className: "vds-callout-v2-icon"
  }, /*#__PURE__*/React__default.createElement(Spinner, {
    intent: "primary",
    size: 16
  })) : IconIncoming !== null && /*#__PURE__*/React__default.createElement(CalloutV2Icon, {
    className: "vds-callout-v2-icon",
    intent: intent
  }, IconIncoming ? /*#__PURE__*/React__default.createElement(IconIncoming, null) : intent === "primary" ? /*#__PURE__*/React__default.createElement(InformationFilled32, null) : intent === "success" ? /*#__PURE__*/React__default.createElement(CheckmarkFilled32, null) : intent === "warning" ? /*#__PURE__*/React__default.createElement(WarningAltFilled32, null) : intent === "danger" ? /*#__PURE__*/React__default.createElement(WarningFilled32, null) : /*#__PURE__*/React__default.createElement(InformationFilled32, null)), title && /*#__PURE__*/React__default.createElement(HTMLHeading, {
    className: "vds-callout-v2-title",
    styleLevel: "h6",
    tagLevel: titleTagName,
    text: title
  }), children), action && actionText && /*#__PURE__*/React__default.createElement(CalloutV2Action, {
    className: "vds-callout-v2-action",
    inPage: inPage
  }, /*#__PURE__*/React__default.createElement(TooltipV2, {
    content: actionText,
    disabled: !hideActionText
  }, /*#__PURE__*/React__default.createElement(Button, {
    className: "vds-callout-v2-action-button",
    icon: actionIcon,
    minimal: true,
    onClick: action,
    small: !inPage,
    text: !hideActionText && actionText
  }))));
});

export { CalloutV2 };
//# sourceMappingURL=calloutV2.js.map
